/**
 * AgentsBar — переключатель между основным агентом и его специалистами.
 *
 * Специалисты (08.08) появились для навыков, которые не влезают в общую
 * инструкцию: у «Нового вида» — расчёт стоимости. Основной агент зовёт их сам,
 * когда вопрос по их части, поэтому в интерфейсе они лежат рядом с ним, а не в
 * отдельном разделе меню: это один агент с несколькими головами.
 *
 * Компонент общий для нашей Тони и для агентов клиентов — отличается только slug
 * родителя.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Loader2, Plus, Trash2, UserCog } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { agentsApi } from '@/api/client'

interface Props {
  parent: string
  /** null — выбран основной агент. */
  selected: string | null
  onSelect: (slug: string | null) => void
  mainLabel?: string
}

export function AgentsBar({ parent, selected, onSelect, mainLabel = 'Основной агент' }: Props) {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [hint, setHint] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: subagents, isLoading } = useQuery({
    queryKey: ['subagents', parent],
    queryFn: () => agentsApi.list(parent),
    staleTime: 30 * 1000,
  })

  const create = useMutation({
    mutationFn: () => agentsApi.create(parent, name.trim(), hint.trim()),
    onSuccess: async (res) => {
      setCreating(false); setName(''); setHint(''); setError(null)
      await qc.invalidateQueries({ queryKey: ['subagents', parent] })
      onSelect(res.slug)
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Не удалось создать агента.'),
  })

  const remove = useMutation({
    mutationFn: (slug: string) => agentsApi.remove(slug),
    onSuccess: async (_res, slug) => {
      await qc.invalidateQueries({ queryKey: ['subagents', parent] })
      if (selected === slug) onSelect(null)
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Не удалось удалить агента.'),
  })

  const list = subagents ?? []

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs ${
            selected === null ? 'border-slate-900 bg-slate-900 text-white' : 'hover:bg-accent'
          }`}
        >
          <Bot className="h-3.5 w-3.5" />
          {mainLabel}
        </button>

        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}

        {list.map((s) => (
          <div
            key={s.slug}
            className={`flex items-center gap-1 rounded-md border pr-1 text-xs ${
              selected === s.slug ? 'border-slate-900 bg-slate-900 text-white' : 'hover:bg-accent'
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(s.slug)}
              className="flex items-center gap-1.5 py-1.5 pl-3"
              title={s.routing_hint || 'Условие вызова не задано'}
            >
              <UserCog className="h-3.5 w-3.5" />
              {s.name}
              <span className={selected === s.slug ? 'text-slate-300' : 'text-muted-foreground'}>
                · {s.topics}
              </span>
            </button>
            <button
              type="button"
              title="Удалить агента вместе с его базой знаний"
              disabled={remove.isPending}
              className={`rounded p-1 ${
                selected === s.slug ? 'text-slate-400 hover:text-red-300' : 'text-slate-400 hover:text-red-600'
              } disabled:opacity-50`}
              onClick={() => {
                if (confirm(`Удалить агента «${s.name}»? Его инструкция, база знаний и история пропадут.`)) {
                  setError(null)
                  remove.mutate(s.slug)
                }
              }}
            >
              {remove.isPending && remove.variables === s.slug
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Trash2 className="h-3 w-3" />}
            </button>
          </div>
        ))}

        {!creating && (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
                  onClick={() => { setCreating(true); setError(null) }}>
            <Plus className="h-3 w-3" />
            Добавить агента
          </Button>
        )}
      </div>

      {creating && (
        <div className="space-y-2 rounded-md border bg-slate-50/60 p-3">
          <input
            autoFocus
            className="w-full rounded border bg-background px-2 py-1.5 text-sm"
            placeholder="Название: например, Калькулятор"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="w-full rounded border bg-background px-2 py-1.5 text-sm"
            placeholder="Когда звать: вопросы о стоимости, расчёт по площади"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Отдельная инструкция и своя база знаний. Основной агент обратится к нему
            сам, когда вопрос подойдёт под описание, и ответит клиенту своим голосом.
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs"
                    disabled={create.isPending || !name.trim()}
                    onClick={() => create.mutate()}>
              {create.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Создать
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => { setCreating(false); setError(null) }}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}
    </div>
  )
}
